// Push Notifications utility for PWA
import axios from 'axios';
import { api } from '../services/api';

const VAPID_PUBLIC_KEY = 'BMD42HdgAi41M1jbxQuly784i0iVAoe4cA7WrOI_pAJglyTE3NObSo8inGFgMq1uKV1pQbG4b0iNSdoz_f4V3E4'; // VAPID public key for push notifications

export interface PushSubscriptionData {
  endpoint: string;
  keys: {
    p256dh: string;
    auth: string;
  };
}

export interface NotificationPayload {
  title: string;
  body: string;
  icon?: string;
  badge?: string;
  url?: string;
  actions?: {
    action: string;
    title: string;
    icon?: string;
  }[];
}

// Check if push notifications are supported
export const isPushNotificationSupported = (): boolean => {
  return (
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
};

// Request notification permission
export const requestNotificationPermission = async (): Promise<NotificationPermission> => {
  if (!('Notification' in window)) {
    throw new Error('This browser does not support notifications');
  }

  const permission = await Notification.requestPermission();
  return permission;
};

// Subscribe to push notifications
export const subscribeToPushNotifications = async (): Promise<PushSubscription | null> => {
  if (!isPushNotificationSupported()) {
    throw new Error('Push notifications are not supported');
  }

  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY) as any
  });

  return subscription;
};

// Unsubscribe from push notifications
export const unsubscribeFromPushNotifications = async (): Promise<boolean> => {
  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription();

  if (subscription) {
    const result = await subscription.unsubscribe();
    return result;
  }

  return false;
};

// Get current push subscription
export const getCurrentPushSubscription = async (): Promise<PushSubscription | null> => {
  if (!isPushNotificationSupported()) {
    return null;
  }

  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription();
  return subscription;
};

// Send subscription to backend
export const sendSubscriptionToBackend = async (subscription: PushSubscription): Promise<void> => {
  const p256dhKey = subscription.getKey('p256dh');
  const authKey = subscription.getKey('auth');

  if (!p256dhKey || !authKey) {
    throw new Error('Missing subscription keys');
  }

  const subscriptionData: PushSubscriptionData = {
    endpoint: subscription.endpoint,
    keys: {
      p256dh: btoa(String.fromCharCode.apply(null, Array.from(new Uint8Array(p256dhKey)))),
      auth: btoa(String.fromCharCode.apply(null, Array.from(new Uint8Array(authKey))))
    }
  };

  // Send to backend API using configured axios instance
  try {
    // Use the same API configuration as the main api.ts file
    const rawBase = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
    const baseURL = /\/api\/?$/i.test(rawBase)
      ? rawBase.replace(/\/$/, '')
      : rawBase.replace(/\/$/, '') + '/api';

    const tempApi = axios.create({
      baseURL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });

    const response = await tempApi.post('/push-notifications/push-subscription', subscriptionData);
  } catch (error: any) {
    // Don't throw error for now to prevent app crash
    // throw new Error('Failed to send subscription to backend');
  }
};

// Initialize push notifications
export const initializePushNotifications = async (): Promise<void> => {
  try {
    // Check if already subscribed
    const existingSubscription = await getCurrentPushSubscription();

    if (existingSubscription) {
      // Already subscribed, send to backend if not already done
      await sendSubscriptionToBackend(existingSubscription);
      return;
    }

    // Request permission
    const permission = await requestNotificationPermission();

    if (permission === 'granted') {
      // Subscribe to push notifications
      const subscription = await subscribeToPushNotifications();

      if (subscription) {
        // Send subscription to backend
        await sendSubscriptionToBackend(subscription);
      }
    }
  } catch (error) {
    console.error('Error initializing push notifications:', error);
  }
};

// Utility function to convert VAPID key
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

// Test notification (for development)
export const showTestNotification = (): void => {
  if ('Notification' in window && Notification.permission === 'granted') {
    new Notification('WabbyCall - Teste', {
      body: 'Esta é uma notificação de teste',
      icon: 'assets/favicon.ico',
      badge: 'assets/favicon.ico'
    });
  }
};